import { Box, Group, Stack, Text } from '@mantine/core';
import { Gauge } from 'lucide-react';
import type { ReactElement } from 'react';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { formatCount } from '@/shared/analytics/MetricFormatting';
import { recordFitScore } from './ModelFitRecord';

interface FitStats {
    min: number;
    avg: number;
    max: number;
    count: number;
}

function fitStats(records: InteractionRecord[]): FitStats | null {
    let min = 100;
    let max = 0;
    let sum = 0;
    let count = 0;
    for (const record of records) {
        const score = recordFitScore(record);
        if (score === null) continue;
        if (score < min) min = score;
        if (score > max) max = score;
        sum += score;
        count += 1;
    }
    if (count === 0) return null;
    return { min, avg: sum / count, max, count };
}

/**
 * The drill-in fit-score widget — the model-fit counterpart of the evidence
 * "Classification Confidence" panel. Shows the average per-prompt fit score
 * (1–100) with a min→max distribution bar. Returns null when no prompt is scored.
 */
export default function ModelFitScorePanel({ records }: { records: InteractionRecord[] }): ReactElement | null {
    const stats = fitStats(records);
    if (stats === null) return null;

    const tokens = {
        solid: 'var(--bm-color-brand-accent)',
        soft: 'var(--bm-color-brand-soft)',
        border: 'var(--bm-color-brand-border)',
        text: 'var(--bm-color-brand-accent)',
    };

    return (
        <SurfaceCard $padding="md">
            <Group gap="sm" mb="md">
                <Box
                    style={{
                        width: 26,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tokens.soft,
                        border: `1px solid ${tokens.border}`,
                        borderRadius: 'var(--bm-radius)',
                    }}
                >
                    <Gauge size={14} color={tokens.solid} />
                </Box>
                <Box>
                    <Text size="sm" fw={700} style={{ letterSpacing: '-0.02em' }}>
                        Model Fit Score
                    </Text>
                    <Text size="xs" c="dimmed" style={{ fontSize: '10px' }}>
                        Per-prompt fit, 1–100
                    </Text>
                </Box>
            </Group>

            <Stack gap="lg">
                <Box>
                    <Group justify="space-between" mb="xs">
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Average fit
                        </Text>
                        <Text size="xs" ff="monospace" fw={700} style={{ color: tokens.text }}>
                            {Math.round(stats.avg)} avg
                        </Text>
                    </Group>
                    <Box style={{ position: 'relative', height: 8, background: 'var(--bm-color-surface)' }}>
                        <Box
                            style={{
                                position: 'absolute',
                                left: `${stats.min}%`,
                                width: `${Math.max(0, stats.max - stats.min)}%`,
                                height: '100%',
                                background: tokens.soft,
                            }}
                        />
                        <Box style={{ position: 'absolute', left: `${stats.avg}%`, width: 2, height: '100%', background: tokens.solid }} />
                    </Box>
                    <Group justify="space-between" mt={4}>
                        <Text c="dimmed" ff="monospace" style={{ fontSize: '9px' }}>
                            min {stats.min}
                        </Text>
                        <Text c="dimmed" ff="monospace" style={{ fontSize: '9px' }}>
                            max {stats.max}
                        </Text>
                    </Group>
                    <Text size="xs" c="dimmed" mt="xs" style={{ fontSize: '10px' }}>
                        {formatCount(stats.count)} of {formatCount(records.length)} prompts scored
                    </Text>
                </Box>
            </Stack>
        </SurfaceCard>
    );
}
